import * as styles from "./TimeRangePickerRange.css";

import { clsx } from "clsx";
import { useRef, useState } from "react";
import type { JSX, KeyboardEvent, PointerEvent } from "react";

import { Icon } from "@frontend/components/atoms/Icon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { formatMinutes } from "@frontend/utils/duration";
import { useLingui } from "@lingui/react/macro";

import { useTimeRangePickerContext } from "./TimeRangePicker.context";
import type {
  MinuteRange,
  TimeRangePickerRangeProps,
} from "./TimeRangePicker.types";
import { clamp, formatRange } from "./timeMath";

type DragMode = "move" | "begin" | "end";

type Drag = {
  pointerId: number;
  mode: DragMode;
  grab: number;
  bounds: MinuteRange;
  moved: boolean;
};

export function TimeRangePickerRange({
  index,
  tooltip = true,
  removable = true,
  className,
  children,
}: TimeRangePickerRangeProps): JSX.Element | null {
  const { t } = useLingui();

  const {
    ranges,
    lanes,
    minDuration,
    step,
    disabled,
    readOnly,
    selectedIndex,
    select,
    updateRange,
    removeRange,
    commit,
    boundsOf,
    minutesAt,
    percentOf,
  } = useTimeRangePickerContext();

  const range = ranges[index] ?? null;
  const lane = lanes[index] ?? { lane: 0, lanes: 1 };

  const editable = !disabled && !readOnly;
  const selected = selectedIndex === index;

  const drag = useRef<Drag | null>(null);
  const [dragging, setDragging] = useState(false);

  if (null === range) {
    return null;
  }

  const duration = range.end - range.begin;

  const startDrag = (event: PointerEvent<HTMLElement>, mode: DragMode) => {
    if (0 !== event.button) {
      return;
    }

    event.stopPropagation();
    select(index);

    if (!editable) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = {
      pointerId: event.pointerId,
      mode,
      grab: minutesAt(event.clientX) - range.begin,
      bounds: boundsOf(index),
      moved: false,
    };
  };

  const handlePointerMove = (event: PointerEvent<HTMLElement>) => {
    const current = drag.current;

    if (!current || current.pointerId !== event.pointerId) {
      return;
    }

    const cursor = minutesAt(event.clientX);
    const { bounds } = current;

    let next: MinuteRange;

    if ("move" === current.mode) {
      const begin = clamp(
        cursor - current.grab,
        bounds.begin,
        bounds.end - duration,
      );

      next = { begin, end: begin + duration };
    } else if ("begin" === current.mode) {
      next = {
        begin: clamp(cursor, bounds.begin, range.end - minDuration),
        end: range.end,
      };
    } else {
      next = {
        begin: range.begin,
        end: clamp(cursor, range.begin + minDuration, bounds.end),
      };
    }

    if (next.begin !== range.begin || next.end !== range.end) {
      if (!current.moved) {
        current.moved = true;
        setDragging(true);
      }

      updateRange(index, next);
    }
  };

  const handlePointerUp = (event: PointerEvent<HTMLElement>) => {
    const current = drag.current;

    if (!current || current.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    drag.current = null;
    setDragging(false);

    if (current.moved) {
      commit();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!editable) {
      return;
    }

    const bounds = boundsOf(index);
    const direction =
      "ArrowLeft" === event.key ? -1 : "ArrowRight" === event.key ? 1 : 0;

    let next: MinuteRange | null = null;

    if (0 !== direction) {
      const delta = direction * step;

      if (event.shiftKey) {
        next = {
          begin: range.begin,
          end: clamp(range.end + delta, range.begin + minDuration, bounds.end),
        };
      } else if (event.altKey) {
        next = {
          begin: clamp(
            range.begin + delta,
            bounds.begin,
            range.end - minDuration,
          ),
          end: range.end,
        };
      } else {
        const begin = clamp(
          range.begin + delta,
          bounds.begin,
          bounds.end - duration,
        );

        next = { begin, end: begin + duration };
      }
    } else if ("Home" === event.key) {
      next = { begin: bounds.begin, end: bounds.begin + duration };
    } else if ("End" === event.key) {
      next = { begin: bounds.end - duration, end: bounds.end };
    } else if ("Delete" === event.key || "Backspace" === event.key) {
      event.preventDefault();
      removeRange(index);
      commit();

      return;
    } else if ("Escape" === event.key) {
      select(null);
      event.currentTarget.blur();

      return;
    }

    if (null === next) {
      return;
    }

    event.preventDefault();
    updateRange(index, next);
    commit();
  };

  const left = percentOf(range.begin);
  const width = percentOf(range.end) - left;

  return (
    <div
      data-slot="time-range-picker-range"
      className={clsx(styles.cell, className)}
      style={{
        left: `${left}%`,
        width: `${width}%`,
        top: `${(lane.lane / lane.lanes) * 100}%`,
        height: `${100 / lane.lanes}%`,
      }}
    >
      <Tooltip disabled={!tooltip || disabled || dragging}>
        <TooltipTrigger
          render={
            <div
              className={styles.block}
              role="option"
              tabIndex={disabled ? -1 : 0}
              aria-selected={selected}
              aria-label={formatRange(range)}
              aria-disabled={disabled || undefined}
              data-selected={selected || undefined}
              data-dragging={dragging || undefined}
              data-readonly={readOnly || undefined}
              data-disabled={disabled || undefined}
              onPointerDown={(event) => startDrag(event, "move")}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onFocus={() => select(index)}
              onKeyDown={handleKeyDown}
            />
          }
        >
          <div
            className={styles.handle}
            data-side="begin"
            onPointerDown={(event) => startDrag(event, "begin")}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          />

          {children ?? (
            <>
              <span className={styles.rangeLabel}>{formatRange(range)}</span>
              <span className={styles.durationLabel}>
                {formatMinutes(duration)}
              </span>
              <span className={styles.verticalLabel}>
                {formatMinutes(duration)}
              </span>
            </>
          )}

          <div
            className={styles.handle}
            data-side="end"
            onPointerDown={(event) => startDrag(event, "end")}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          />

          {removable && editable && selected && !dragging ? (
            <button
              type="button"
              tabIndex={-1}
              className={styles.remove}
              aria-label={t`Remove this range`}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => {
                removeRange(index);
                commit();
              }}
            >
              <Icon name="X" size={12} />
            </button>
          ) : null}
        </TooltipTrigger>

        <TooltipContent>
          {formatRange(range)} · {formatMinutes(duration)}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
